import UiButton from "components/base/UiButton";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import { Proposal, ProposalLayout, PreviewProposalMutationVariables } from "generated/graphql";
import { mapContentSettingsInput } from "DDD/action-objects/ProposalSend";
import usePreviewProposal from "hooks/proposals/usePreviewProposal";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import { getMissingJobSizeMessage } from "modules/proposals/validators";
import { useCallback, useState } from "react";

type PreviewProposalButtonProps = {
  proposal: Proposal;
  disabled?: boolean;
  buildVariables?: () => Partial<PreviewProposalMutationVariables>;
};

export default function PreviewProposalButton({
  proposal,
  disabled,
  buildVariables,
}: PreviewProposalButtonProps) {
  const [previewProposal, { isSuccess }] = usePreviewProposal();
  const [previewing, setPreviewing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [jobSizeError, setJobSizeError] = useState<string | null>(null);

  const getVars = useCallback(
    (forceSend?: boolean): PreviewProposalMutationVariables => {
      const base = buildVariables
        ? buildVariables()
        : {
            proposalId: proposal.id,
            layout: (proposal.layout ?? ProposalLayout.SIMPLE) as ProposalLayout,
            emails: [proposal.clientEmailOverride || proposal.contact?.email || ""],
            removeUnitPriceAndQuantity: proposal.removeUnitPriceAndQuantity ?? false,
            removeIndividualLineItemPricing: proposal.removeIndividualLineItemPricing ?? false,
            attachments: proposal.attachments?.map((a: any) => a.id) ?? [],
            contentSettings: mapContentSettingsInput(
              proposal.contentSettings,
              proposal.contentSettings
                ?.filter((item) => item.isEnabled)
                .map((item) => item.id) ?? []
            ),
            emailSubjectOverride: proposal.emailSubjectOverride,
            emailBodyOverride: proposal.emailBodyOverride,
            clientEmailOverride: proposal.clientEmailOverride,
            acceptanceOverride: proposal.acceptanceOverride,
          };
      return {
        proposalId: proposal.id,
        layout: (proposal.layout ?? ProposalLayout.SIMPLE) as ProposalLayout,
        ...base,
        ...(forceSend ? { forceSend: true } : {}),
      };
    },
    [proposal, buildVariables]
  );

  const executePreview = useCallback(
    async (forceSend?: boolean) => {
      setPreviewing(true);
      try {
        const result = await previewProposal({ variables: getVars(forceSend) });
        if (isSuccess(result.data) && result.data?.previewProposal) {
          window.open(result.data.previewProposal, "_blank");
        } else if (
          (result as any)?.name === "ApolloError" &&
          (result as any)?.message?.indexOf("Please input a job size.") !== -1
        ) {
          setJobSizeError((result as any).message);
          setConfirmDialogOpen(true);
        }
      } finally {
        setPreviewing(false);
      }
    },
    [previewProposal, isSuccess, getVars]
  );

  const handleClick = useCallback(() => {
    const msg = getMissingJobSizeMessage(proposal);
    if (msg) {
      setJobSizeError(msg);
      setConfirmDialogOpen(true);
      return;
    }
    executePreview();
  }, [proposal, executePreview]);

  const acceptFn = useCallback(async () => {
    await executePreview(true);
    return true;
  }, [executePreview]);

  const cancelFn = useCallback(async () => true, []);
  const { onAccept, onCancel } = useConfirmationDialog({ acceptFn, cancelFn });

  return (
    <>
      <UiButton uiVariant="primary-grey" onClick={handleClick} disabled={disabled || previewing}>
        Preview Proposal
      </UiButton>
      <ActionConfirmDialog
        open={confirmDialogOpen}
        setOpen={setConfirmDialogOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        content={
          jobSizeError?.replace("Please input a job size.", "Proceed?") ||
          "Are you sure you want to proceed?"
        }
      />
    </>
  );
}
